import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Pencil } from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Champ, ChampPuces, ChampTexte, MentionObligatoire } from "@/composants/ui/champ";
import { Chargement, Erreur, Vide } from "@/composants/ui/etats";
import { Donnee, Etiquette } from "@/composants/ui/etiquette";
import { messageDe, useChargement } from "@/lib/chargement";
import {
  dateCourte,
  euros,
  OPTIONS_CIVILITE,
  statutCommande,
  typeClient,
} from "@/lib/libelles";
import { api, type Client, type FicheClient } from "@/lib/api";
import { useSession } from "@/session";

/** La fiche d'un client et la liste de ses dossiers. */
export function PageClient() {
  const { id = "" } = useParams();
  const session = useSession();
  const identite = session.statut === "connecte" ? session.identite : null;
  const aLaPermission = identite?.permissions.includes("GERER_COMMANDE_CLIENT") ?? false;
  const voitLesMontants = identite?.permissions.includes("CONSULTER_MONTANTS") ?? false;

  const { donnees, erreur, enCours, recharger } = useChargement<FicheClient>(
    (signal) => api.client(id, signal),
    [id],
  );
  const [corrige, setCorrige] = useState<Client | null>(null);
  const [correction, setCorrection] = useState(false);
  const [action, setAction] = useState<string | null>(null);

  if (erreur) return <Erreur message={erreur} onReessayer={recharger} />;
  if (!donnees || enCours) return <Chargement message="Chargement du client…" />;

  const client = corrige ?? donnees.client;
  // Corriger un client, c'est agir sur l'une de ses commandes (ADR-0032).
  const peutGerer = aLaPermission && donnees.intervenable;
  // Les commandes se présentent par affaire (ADR-0028) : cuisine puis salle de
  // bains d'un même dossier se lisent ensemble. L'ordre du serveur est conservé.
  const affaires = Array.from(
    donnees.commandes.reduce((groupes, commande) => {
      groupes.set(commande.affaire_id, [...(groupes.get(commande.affaire_id) ?? []), commande]);
      return groupes;
    }, new Map<string, typeof donnees.commandes>()),
  );

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/clients"
        className="inline-flex items-center gap-1.5 text-sm text-encre-douce hover:text-encre"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Tous les clients
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <h1 className="text-xl font-semibold text-encre">{client.designation}</h1>
        <Etiquette ton={client.anonymise ? "alerte" : "neutre"}>
          {client.anonymise ? "Fiche anonymisée" : typeClient(client.type)}
        </Etiquette>
      </div>

      {action ? <Erreur message={action} /> : null}

      <Carte>
        <EnTeteCarte className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-encre">Coordonnées</h2>
          {peutGerer && !client.anonymise ? (
            <Bouton taille="compact" variante="discret" onClick={() => setCorrection((o) => !o)}>
              <Pencil className="h-3.5 w-3.5" aria-hidden />
              {correction ? "Fermer" : "Corriger"}
            </Bouton>
          ) : null}
        </EnTeteCarte>
        <CorpsCarte>
          {correction ? (
            <FormulaireClient
              client={client}
              onAnnuler={() => setCorrection(false)}
              onValider={async (champs) => {
                setAction(null);
                try {
                  setCorrige(await api.corrigerClient(client.id, champs));
                  setCorrection(false);
                } catch (cause) {
                  setAction(messageDe(cause));
                }
              }}
            />
          ) : (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-4 sm:grid-cols-3">
              <Donnee libelle="Adresse électronique">{client.email ?? "—"}</Donnee>
              <Donnee libelle="Téléphone">{client.telephone ?? "—"}</Donnee>
              <Donnee libelle="Téléphone secondaire">{client.telephone_secondaire ?? "—"}</Donnee>
              <Donnee libelle="Adresse">
                {client.adresse_ligne1 ?? "—"}
                {client.adresse_ligne2 ? <br /> : null}
                {client.adresse_ligne2}
                {client.code_postal || client.ville ? <br /> : null}
                {[client.code_postal, client.ville].filter(Boolean).join(" ")}
              </Donnee>
              {client.notes ? (
                <div className="col-span-full">
                  <Donnee libelle="Notes">{client.notes}</Donnee>
                </div>
              ) : null}
            </dl>
          )}
        </CorpsCarte>
      </Carte>

      <h2 className="text-sm font-semibold text-encre">
        {donnees.commandes.length} commande{donnees.commandes.length > 1 ? "s" : ""}
        {affaires.length > 1 ? `, ${affaires.length} affaires` : ""}
      </h2>

      {donnees.commandes.length === 0 ? (
        <Carte>
          <Vide titre="Aucune Commande / Projet visible"
            detail="Aucune Commande / Projet de ce client n'est dans votre périmètre." />
        </Carte>
      ) : (
        affaires.map(([affaireId, commandes]) => (
        <Carte key={affaireId}>
          <EnTeteCarte>
            <Link
              to={`/affaires/${affaireId}`}
              className="font-mono text-sm text-encre underline underline-offset-4"
            >
              {commandes[0].numero_affaire}
            </Link>
          </EnTeteCarte>
          <ul className="divide-y divide-filet">
            {commandes.map((commande) => (
              <li key={commande.id}>
                <Link
                  to={`/commandes/${commande.id}`}
                  className="flex flex-col gap-1 px-4 py-3 hover:bg-chene-clair/25 sm:flex-row sm:items-center sm:gap-4"
                >
                  <span className="font-mono text-sm text-encre sm:w-28 sm:shrink-0">
                    {commande.numero_commande_client}
                  </span>
                  <span className="flex-1 text-sm text-encre-douce">
                    {dateCourte(commande.date_commande)}
                  </span>
                  {voitLesMontants ? (
                    <span className="text-sm text-encre-douce sm:w-28 sm:shrink-0 sm:text-right sm:tabular-nums">
                      {euros(commande.montant_ht)}
                    </span>
                  ) : null}
                  <span className="text-sm text-encre-douce sm:w-24 sm:shrink-0">
                    {commande.nombre_lots} lot{commande.nombre_lots > 1 ? "s" : ""}
                  </span>
                  <span className="sm:w-44 sm:shrink-0">
                    <Etiquette>{statutCommande(commande.statut)}</Etiquette>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Carte>
        ))
      )}
    </div>
  );
}

function FormulaireClient({
  client,
  onValider,
  onAnnuler,
}: {
  client: Client;
  onValider: (champs: Record<string, unknown>) => Promise<void>;
  onAnnuler: () => void;
}) {
  const [valeurs, setValeurs] = useState({
    civilite: client.civilite ?? "",
    nom: client.nom,
    prenom: client.prenom ?? "",
    raison_sociale: client.raison_sociale ?? "",
    email: client.email ?? "",
    telephone: client.telephone ?? "",
    telephone_secondaire: client.telephone_secondaire ?? "",
    adresse_ligne1: client.adresse_ligne1 ?? "",
    adresse_ligne2: client.adresse_ligne2 ?? "",
    code_postal: client.code_postal ?? "",
    ville: client.ville ?? "",
    notes: client.notes ?? "",
  });
  const [envoi, setEnvoi] = useState(false);
  const poser = (champ: keyof typeof valeurs) => (valeur: string) =>
    setValeurs({ ...valeurs, [champ]: valeur });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        try {
          // Une chaîne vide efface le champ ; c'est une correction légitime,
          // et `null` est ce que la base attend pour « rien ».
          //
          // La civilité d'un professionnel n'est PAS envoyée : le formulaire ne
          // la montre pas, il n'a donc pas à la toucher. Le point d'API ne
          // modifie que les champs reçus — un champ absent reste ce qu'il est.
          const champs = Object.entries(valeurs)
            .filter(([cle]) => !(cle === "civilite" && client.type === "PROFESSIONNEL"))
            .map(([cle, valeur]) => [cle, valeur === "" ? null : valeur]);
          await onValider(Object.fromEntries(champs));
        } finally {
          setEnvoi(false);
        }
      }}
    >
      <MentionObligatoire />
      <div className="grid gap-4 sm:grid-cols-2">
        {client.type === "PROFESSIONNEL" ? (
          <Champ
            libelle="Raison sociale"
            required
            value={valeurs.raison_sociale}
            onChange={(e) => poser("raison_sociale")(e.target.value)}
          />
        ) : (
          <ChampPuces
            libelle="Civilité"
            options={OPTIONS_CIVILITE}
            valeur={valeurs.civilite}
            onChanger={poser("civilite")}
          />
        )}
        <Champ
          libelle="Nom"
          required
          value={valeurs.nom}
          onChange={(e) => poser("nom")(e.target.value)}
        />
        <Champ libelle="Prénom" value={valeurs.prenom} onChange={(e) => poser("prenom")(e.target.value)} />
        <Champ
          libelle="Adresse électronique"
          type="email"
          value={valeurs.email}
          onChange={(e) => poser("email")(e.target.value)}
        />
        <Champ
          libelle="Téléphone"
          type="tel"
          value={valeurs.telephone}
          onChange={(e) => poser("telephone")(e.target.value)}
        />
        <Champ
          libelle="Téléphone secondaire"
          type="tel"
          value={valeurs.telephone_secondaire}
          onChange={(e) => poser("telephone_secondaire")(e.target.value)}
        />
        <Champ
          libelle="Adresse"
          className="sm:col-span-2"
          value={valeurs.adresse_ligne1}
          onChange={(e) => poser("adresse_ligne1")(e.target.value)}
        />
        <Champ
          libelle="Complément d'adresse"
          className="sm:col-span-2"
          value={valeurs.adresse_ligne2}
          onChange={(e) => poser("adresse_ligne2")(e.target.value)}
        />
        <Champ
          libelle="Code postal"
          value={valeurs.code_postal}
          onChange={(e) => poser("code_postal")(e.target.value)}
        />
        <Champ libelle="Ville" value={valeurs.ville} onChange={(e) => poser("ville")(e.target.value)} />
        <ChampTexte
          libelle="Notes"
          className="sm:col-span-2"
          value={valeurs.notes}
          onChange={(e) => poser("notes")(e.target.value)}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" variante="principal" disabled={envoi}>
          {envoi ? "Enregistrement…" : "Enregistrer"}
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler} disabled={envoi}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}
